import { forwardRef, useImperativeHandle, useRef, useCallback, memo, useEffect, useMemo, useState } from 'react';
import { Excalidraw as ExcalidrawComponent } from '@excalidraw/excalidraw';
import { Loader2 } from 'lucide-react';
import "../styles/excalidraw.css";
import { toast } from './ui/use-toast';

interface OptimizedExcalidrawProps {
  initialElements: any[];
  initialAppState: any;
  onChange: (elements: readonly any[], appState: any, files: any) => void;
  isCollaborating?: boolean;
  viewModeEnabled?: boolean;
  zenModeEnabled?: boolean;
  gridModeEnabled?: boolean;
  isReadOnly?: boolean;
  onImageUpload?: (file: File) => Promise<string>; // URL or data URL
}

interface ExcalidrawAPI {
  updateScene: (data: { elements: any[], appState: any }) => void;
}

interface SceneData {
  elements: any[]; // Changed from readonly any[] to mutable
  appState: any;
  files?: any;
}

const OptimizedExcalidraw = memo(forwardRef<ExcalidrawAPI, OptimizedExcalidrawProps>(({
  initialElements,
  initialAppState,
  onChange,
  isCollaborating = false,
  viewModeEnabled = false,
  zenModeEnabled = false,
  gridModeEnabled = false,
  isReadOnly = false,
  onImageUpload
}, ref) => {
  // Extend the ExcalidrawAPI type to include updateLibrary
  interface ExtendedExcalidrawAPI extends ExcalidrawAPI {
    updateLibrary: (data: { libraryItems: any[] }) => void;
  }
  
  const excalidrawRef = useRef<ExtendedExcalidrawAPI | null>(null);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastUpdateTime = useRef<number>(0);
  
  // Handle library import from URL
  const loadLibraryFromUrl = useCallback(async (url: string) => {
    try {
      setIsLoadingLibrary(true);
      setLibraryError(null);
      
      // Ensure the URL is absolute
      const absoluteUrl = url.startsWith('http') 
        ? url 
        : `https://${url.replace(/^\/\//, '')}`;
      
      const response = await fetch(absoluteUrl, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load library: ${response.statusText}`);
      }
      
      const data = await response.json();
      const libraryItems = Array.isArray(data) ? data : (data.library || []);
      
      if (excalidrawRef.current) {
        // Use the libraryItems prop to update the library
        excalidrawRef.current.updateLibrary({ libraryItems });
      }
      
      return true;
    } catch (error) {
      console.error('Error loading library:', error);
      setLibraryError(error instanceof Error ? error.message : 'Failed to load library');
      return false;
    } finally {
      setIsLoadingLibrary(false);
    }
  }, [excalidrawRef]);
  
  // Handle URL hash changes for library imports
  useEffect(() => {
    const handleHashChange = (e?: HashChangeEvent) => {
      // Only process if this is a library import
      if (window.location.hash.startsWith('#addLibrary=')) {
        // Prevent the default hash change behavior
        if (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }

        // Extract the library URL
        const hash = window.location.hash;
        const currentUrl = new URL(window.location.href);
        const libraryUrl = decodeURIComponent(hash.replace('#addLibrary=', '').split('&')[0]);
        
        if (libraryUrl) {
          // Store the current URL to restore later
          const currentPath = window.location.pathname;
          const currentSearch = currentUrl.search;
          
          // Load the library
          loadLibraryFromUrl(libraryUrl).finally(() => {
            // After loading, restore the original URL with search params
            const newUrl = currentPath + currentSearch;
            window.history.replaceState(null, '', newUrl);
          });
        }
      }
    };

    // Initial check
    handleHashChange();

    // Listen for hash changes with capture to handle it before React Router
    window.addEventListener('hashchange', handleHashChange, true);
    
    // Also listen for popstate to handle back/forward navigation
    const handlePopState = () => {
      if (window.location.hash.startsWith('#addLibrary=')) {
        handleHashChange();
      }
    };
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [loadLibraryFromUrl]);

  // Handle image paste
  const handlePaste = useCallback(async (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        event.preventDefault();
        
        const file = items[i].getAsFile();
        if (!file) return;
        
        try {
          setIsUploadingImage(true);
          
          // If there's a custom upload handler, use it
          if (onImageUpload) {
            const imageUrl = await onImageUpload(file);
            if (excalidrawRef.current) {
              excalidrawRef.current.updateScene({
                elements: [],
                appState: { ...initialAppState, currentItemFontFamily: 1 },
                files: { [file.name]: { id: file.name, dataURL: imageUrl } }
              } as any); // Type assertion as we know the internal type
            }
          } else {
            // Fallback to data URL
            const reader = new FileReader();
            reader.onload = (e) => {
              if (excalidrawRef.current && e.target?.result) {
                excalidrawRef.current.updateScene({
                  elements: [],
                  appState: { ...initialAppState, currentItemFontFamily: 1 },
                  files: { [file.name]: { id: file.name, dataURL: e.target.result as string } }
                } as any); // Type assertion as we know the internal type
              }
            };
            reader.readAsDataURL(file);
          }
          
          toast({
            title: 'Image pasted',
            description: 'The image has been added to the board',
          });
          
        } catch (error) {
          console.error('Error pasting image:', error);
          toast({
            title: 'Error',
            description: 'Failed to paste image',
            variant: 'destructive',
          });
        } finally {
          setIsUploadingImage(false);
        }
        break;
      }
    }
  }, [onImageUpload, initialAppState]);

  // Set up paste event listener
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const styles = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  const isMounted = useRef(true);

  // Handle changes from the Excalidraw component
  const handleChange = useCallback((elements: readonly any[], appState: any, files: any) => {
    // Skip update if the component is not mounted
    if (!isMounted.current) return;
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime.current;
    
    // If we have a pending update, clear it
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // If it's been more than 30ms since the last update, update immediately
    // This provides a good balance between performance and responsiveness
    if (timeSinceLastUpdate > 30) {
      lastUpdateTime.current = now;
      try {
        onChange(elements, appState, files);
      } catch (error) {
        console.error('Error in handleChange:', error);
      }
    } else {
      // Otherwise, schedule an update
      updateTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          lastUpdateTime.current = Date.now();
          try {
            onChange(elements, appState, files);
          } catch (error) {
            console.error('Error in handleChange (delayed):', error);
          }
        }
      }, 30 - timeSinceLastUpdate);
    }
  }, [onChange]);

  // Create a stable updateScene function with better batching
  const updateScene = useCallback((data: SceneData) => {
    if (!excalidrawRef.current) return;
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime.current;
    
    // If updates are coming in too fast, batch them
    if (timeSinceLastUpdate < 50) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = setTimeout(() => {
        if (isMounted.current && excalidrawRef.current) {
          lastUpdateTime.current = Date.now();
          excalidrawRef.current.updateScene({
            elements: Array.isArray(data.elements) ? [...data.elements] : [],
            appState: data.appState ? { ...data.appState } : {},
            ...(data.files && { files: data.files })
          });
        }
      }, 50 - timeSinceLastUpdate);
      return;
    }
    
    // Otherwise, update immediately
    lastUpdateTime.current = now;
    try {
      excalidrawRef.current.updateScene({
        elements: Array.isArray(data.elements) ? [...data.elements] : [],
        appState: data.appState ? { ...data.appState } : {},
        ...(data.files && { files: data.files })
      });
    } catch (error) {
      console.error('Error in updateScene:', error);
    }
  }, []);

  useEffect(() => {
    if (!excalidrawRef.current) return;
    
    const now = Date.now();
    if (now - lastUpdateTime.current < 100) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          excalidrawRef.current?.updateScene({
            elements: [...initialElements], 
            appState: { ...initialAppState }
          });
        }
      }, 100 - (now - lastUpdateTime.current));
      return;
    }
    
    lastUpdateTime.current = now;
    excalidrawRef.current.updateScene({
      elements: [...initialElements], 
      appState: { ...initialAppState }
    });
  }, [initialElements, initialAppState]);

  // Expose API methods
  useImperativeHandle(ref, () => ({
    ...(excalidrawRef.current || {}),
    updateScene
  }), [updateScene]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      
      // Clear any pending timeouts
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = undefined;
      }
      
      // Reset states
      setIsLoadingLibrary(false);
      setLibraryError(null);
      setIsUploadingImage(false);
      
      // Clear the Excalidraw ref to prevent memory leaks
      excalidrawRef.current = null;
    };
  }, []);

  // Memoize the initial data to prevent unnecessary re-renders
  const initialData = useMemo(() => ({
    elements: initialElements,
    appState: {
      ...initialAppState,
      viewBackgroundColor: '#fafafa',
      currentItemFontFamily: 1,
      currentItemRoundness: 'round',
      currentItemStrokeColor: '#1e1e1e',
      currentItemBackgroundColor: 'transparent',
      currentItemFillStyle: 'hachure',
      currentItemStrokeWidth: 1,
      currentItemOpacity: 100,
      currentItemTextAlign: 'left',
      currentItemStrokeStyle: 'solid',
      currentItemRoughness: 1,
      currentItemLinearStrokeSharpness: 'round',
      currentItemStartArrowhead: null,
      currentItemEndArrowhead: null,
      currentItemRounded: false,
      currentItemLineHeight: 1.25,
      currentItemFontSize: 16,
      currentItemFont: 1,
      currentItemText: '',
      currentItemStrokeSharpness: 'sharp',
    },
  }), [initialElements, initialAppState]);

  return (
    <div 
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        zIndex: 1,
        isolation: 'isolate'
      }}
      className="excalidraw-container"
    >
      {isLoadingLibrary && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div className="spinner" style={{
            width: '24px',
            height: '24px',
            border: '3px solid rgba(0, 0, 0, 0.1)',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span>Loading library...</span>
        </div>
      )}
      
      {libraryError && (
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#fee2e2',
          color: '#b91c1c',
          padding: '8px 16px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '80%',
          textAlign: 'center'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{libraryError}</span>
          <button 
            onClick={() => setLibraryError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '4px',
              marginLeft: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
      
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        padding: 0,
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {isUploadingImage && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '1rem',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Uploading image...</span>
        </div>
      )}

      <style>
        {`
          .excalidraw .LoadingMessage {
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
          
          /* Main container adjustments */
          .excalidraw {
            --sidebar-width: 44px;
            --right-panel-width: 0px; /* We'll handle the right panel in the parent */
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
          }
          
          /* Ensure the main UI takes full width */
          .excalidraw .layer-ui__wrapper {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 100% !important;
            display: flex !important;
            flex-direction: row !important;
            contain: layout style;
            overflow: visible !important;
          }
          
          /* Ensure help dialog is visible */
          .excalidraw .help-dialog {
            z-index: 10000 !important;
            position: fixed !important;
            top: 5% !important;
            left: 5% !important;
            right: 5% !important;
            bottom: 5% !important;
            width: 90% !important;
            height: 90% !important;
            margin: 0 !important;
            padding: 0 !important;
            transform: none !important;
            display: flex !important;
            flex-direction: column !important;
            contain: layout style paint !important;
            transform-origin: center center !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
            max-width: none !important;
            max-height: none !important;
          }
          
          /* Fix dialog portal container */
          .excalidraw .dialog-container {
            position: fixed !important;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 10000 !important;
            display: flex !important;
            align-items: center;
            justify-content: center;
            background-color: transparent !important;
            pointer-events: auto !important;
          }
          
          /* Fix for modal dialogs */
          .excalidraw .Modal {
            z-index: 10001 !important;
            position: fixed !important;
            top: 5% !important;
            left: 5% !important;
            right: 5% !important;
            bottom: 5% !important;
            width: 90% !important;
            height: 90% !important;
            background: transparent !important;
            transform: none !important;
          }
          
          /* Remove modal overlay */
          .excalidraw .Modal .Modal__overlay {
            display: none !important;
          }
          
          /* Fix for dropdowns and popovers */
          .excalidraw .dropdown-menu,
          .excalidraw .popover {
            z-index: 10002 !important;
          }
          
          /* Fix for dialog overlay */
          .excalidraw .Modal .Island {
            position: relative !important;
            max-height: 90vh !important;
            overflow-y: auto !important;
            display: block !important;
          }
          
          /* Ensure dialog content is visible */
          .excalidraw .help-dialog-content,
          .excalidraw .Modal__content {
            position: relative !important;
            z-index: 10001 !important;
            background: #ffffff !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
            overflow: hidden !important;
            color: #000 !important;
            padding: 0 !important;
            width: 100% !important;
            height: 100% !important;
            max-width: none !important;
            max-height: none !important;
            display: flex !important;
            flex-direction: column !important;
            pointer-events: auto !important;
            transform: none !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* Fix dialog content container */
          .excalidraw .help-dialog-content > div,
          .excalidraw .Modal__content > div {
            position: relative !important;
            z-index: 1 !important;
            width: 100% !important;
            height: 100% !important;
            overflow: auto !important;
            padding: 2rem !important;
            display: flex !important;
            flex-direction: column !important;
          }
          
          /* Help dialog header */
          .excalidraw .help-dialog h2 {
            font-size: 1.5rem !important;
            margin: 0 0 1.5rem 0 !important;
            padding-bottom: 1rem !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          
          /* Help dialog content */
          .excalidraw .help-dialog-content .dialog-content {
            flex: 1 !important;
            overflow-y: auto !important;
            padding-right: 1rem !important;
          }
          
          /* Help dialog close button */
          .excalidraw .help-dialog .close-button {
            position: absolute !important;
            top: 1rem !important;
            right: 1rem !important;
            background: none !important;
            border: none !important;
            font-size: 1.5rem !important;
            cursor: pointer !important;
            padding: 0.5rem !important;
            line-height: 1 !important;
            z-index: 10 !important;
          }
          
          /* Fix for help dialog content text */
          .excalidraw .help-dialog-content *,
          .excalidraw .Modal__content * {
            color: #000 !important;
            opacity: 1 !important;
          }
          
          /* Fix for dialog close button */
          .excalidraw .Modal__close {
            color: #000 !important;
            opacity: 1 !important;
            position: absolute !important;
            top: 10px !important;
            right: 10px !important;
            z-index: 1000 !important;
          }
          
          /* Main content area */
          .excalidraw .excalidraw-contextMenuContainer {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: auto !important;
            height: auto !important;
            box-sizing: border-box !important;
          }
          
          /* Toolbar container */
          .excalidraw .layer-ui__wrapper__sidebar {
            position: absolute !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            left: auto !important;
            width: 44px !important;
            border-left: 1px solid #e5e7eb !important;
            background: #fafafa !important;
            z-index: 10 !important;
            transform: none !important;
            opacity: 1 !important;
            visibility: visible !important;
            contain: layout style;
            overflow: visible !important;
          }
          
          /* Fix for popovers and tooltips */
          .excalidraw .popover {
            position: fixed !important;
            z-index: 100000 !important;
            max-height: 90vh !important;
            overflow-y: auto !important;
            contain: none !important;
          }
          
          /* Library panel styles */
          .excalidraw .library-menu-items-container {
            z-index: 10 !important;
          }
          

          

          
          /* Toolbar and library button */
          .excalidraw .ToolIcon__library {
            position: relative !important;
            z-index: 5 !important;
          }
          

          

          
          /* Ensure toolbar is properly positioned */
          .excalidraw .layer-ui__wrapper__sidebar {
            z-index: 10 !important;
          }
          

          

          
          /* Ensure popovers stay within viewport */
          .excalidraw .popover .popover-content {
            max-height: 70vh !important;
            overflow-y: auto !important;
          }
          
          /* Top menu bar */
          .excalidraw .App-menu_top {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 44px !important; /* Space for the toolbar */
            height: 44px !important;
            z-index: 11 !important;
            background: #fafafa !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          
          /* Ensure canvas area is properly positioned */
          .excalidraw .excalidraw-contextMenuContainer > div {
            position: absolute !important;
            top: 44px !important; /* Below the top menu */
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          
          /* Fix for tooltip positioning */
          .excalidraw .ToolIcon__tooltip {
            z-index: 100001 !important;
          }
          
          .excalidraw {
            --default-bg-color: #fafafa !important;
            --color-surface-mid: #f5f5f5 !important;
            --color-surface-high: #fff !important;
            --color-surface-low: #f5f5f5 !important;
            --color-surface-hover: rgba(0, 0, 0, 0.04) !important;
            --color-surface-active: rgba(0, 0, 0, 0.06) !important;
            --color-surface-selected: rgba(0, 0, 0, 0.06) !important;
            --color-icon: #444 !important;
            --color-text: #333 !important;
            --color-text-hover: #000 !important;
            --color-text-active: #000 !important;
            --color-text-selected: #000 !important;
            --default-border-color: #e0e0e0 !important;
          }
          
          .excalidraw .excalidraw-canvas-container {
            background: #fafafa !important;
          }
          
          .excalidraw .excalidraw-canvas {
            background: #fafafa !important;
          }
          
          /* Fix cursor positioning */
          .excalidraw .excalidraw-canvas-container {
            transform: none !important;
          }
          
          .excalidraw .excalidraw-canvas {
            transform: none !important;
          }
          
          /* Fix for cursor offset */
          .excalidraw .excalidraw-canvas-container {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
          }
          
          .excalidraw .excalidraw-canvas {
            position: absolute;
            top: 0;
            left: 0;
          }
          
          .excalidraw .scroll-back-to-content {
            display: none !important;
          }
          
          /* Optimize rendering performance */
          .excalidraw {
            /* Removed transform properties that could affect cursor positioning */
            will-change: auto;
          }
          
          .excalidraw .excalidraw-canvas-container {
            /* Ensure canvas container is properly positioned */
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            will-change: auto;
          }
          
          .excalidraw .excalidraw-canvas {
            /* Ensure canvas is properly positioned */
            position: absolute;
            top: 0;
            left: 0;
            will-change: auto;
          }
          
          /* Fix for help dialog content */
          .excalidraw .help-dialog-content {
            max-height: 80vh;
            overflow-y: auto;
          }
        `}
      </style>
      <ExcalidrawComponent
        excalidrawAPI={(api: any) => {
          if (api) {
            // Extend the API with our custom updateLibrary method
            excalidrawRef.current = {
              ...api,
              updateLibrary: (data: { libraryItems: any[] }) => {
                // Update the scene with new library items
                api.updateScene({
                  appState: {
                    ...api.getAppState(),
                    libraryItems: data.libraryItems
                  }
                });
              }
            };
          }
        }}
        initialData={initialData}
        onChange={handleChange}
        isCollaborating={isCollaborating}
        viewModeEnabled={viewModeEnabled}
        zenModeEnabled={zenModeEnabled}
        gridModeEnabled={gridModeEnabled}
        UIOptions={{
          canvasActions: {
            loadScene: false,
            saveToActiveFile: false,
            toggleTheme: true,
            clearCanvas: true,
            export: false,
            saveAsImage: false,
          },
        }}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        name="Collaborative Board"
      />
      </div>
    </div>
  );
}));

OptimizedExcalidraw.displayName = 'OptimizedExcalidraw';

export { OptimizedExcalidraw };
export default OptimizedExcalidraw;
